/**
 * Admin wizard help/how-to page for /admin/wizards/help route.
 * Reference documentation for the wizard system concepts and configuration.
 */

import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/layout/PageHeader";
import { useProfile } from "../contexts/ProfileContext";
import { adminRoutes, publicRoutes } from "../lib/routes";
import {
  BookOpen,
  Wand2,
  Blocks,
  ListOrdered,
  Users,
  Link2,
  Settings2,
  Info,
  AlertTriangle,
  LayoutTemplate,
  Play,
  FormInput,
  Zap,
  FileText,
  Timer,
  PartyPopper,
} from "lucide-react";

export function AdminWizardHelp() {
  const { t } = useTranslation();
  const { isAdmin, isLoading: isProfileLoading } = useProfile();

  if (!isProfileLoading && !isAdmin) {
    return <Navigate to={publicRoutes.home} replace />;
  }

  return (
    <div>
      <PageHeader
        title={t("adminWizards.help.title", { defaultValue: "Wizard Help" })}
        description={t("adminWizards.help.description", { defaultValue: "Reference guide for the wizard system" })}
        isAdmin
        breadcrumbs={[
          { label: t("adminWizards.breadcrumbs.dashboard"), href: publicRoutes.home },
          { label: t("adminWizards.breadcrumbs.admin") },
          { label: t("adminWizards.breadcrumbs.wizards"), href: adminRoutes.wizards },
          { label: "Help" },
        ]}
      />

      <div className="space-y-8">
        {/* 1. Overview */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="h-5 w-5 text-nara-orange" />
            <h2 className="text-lg font-semibold text-foreground">Overview</h2>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <p className="text-sm text-muted-foreground leading-relaxed">
              The wizard system lets you create guided step-by-step flows for users.
              Build templates from reusable blocks, assign instances to specific users,
              and track their progress. Wizards are used for onboarding, data collection,
              workspace setup, and any multi-step guided process.
            </p>
          </div>
        </section>

        {/* 2. Key Concepts */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Wand2 className="h-5 w-5 text-nara-orange" />
            <h2 className="text-lg font-semibold text-foreground">Key Concepts</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ConceptCard
              icon={<LayoutTemplate className="h-5 w-5 text-nara-orange" />}
              title="Template"
              description="The blueprint. Defines the sequence of steps, their configuration, and metadata. Templates can be active or inactive. An active template is available for public slug-based access."
            />
            <ConceptCard
              icon={<Blocks className="h-5 w-5 text-nara-orange" />}
              title="Block"
              description="A reusable step type registered in the system (e.g., form, splash, polling). Blocks define what a step does. Each block type has its own UI behavior and configuration options."
            />
            <ConceptCard
              icon={<ListOrdered className="h-5 w-5 text-nara-orange" />}
              title="Step"
              description="A configured instance of a block within a template. Steps have a sort order, optional config overrides (title, points, fields, etc.), timeout, and skippable flag."
            />
            <ConceptCard
              icon={<Users className="h-5 w-5 text-nara-orange" />}
              title="Instance"
              description="A specific run of a wizard for a specific user. Created from a template, it receives a frozen snapshot of the template at creation time. Changes to the template won't affect existing instances."
            />
            <ConceptCard
              icon={<Link2 className="h-5 w-5 text-nara-orange" />}
              title="Slug"
              description='A URL-friendly handle for the template (e.g., "onboarding"). Used in public URLs: /wizard/s/onboarding. When a user visits a slug URL, an instance is automatically provisioned for them.'
            />
          </div>
        </section>

        {/* 3. How to Create a Wizard */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Wand2 className="h-5 w-5 text-nara-orange" />
            <h2 className="text-lg font-semibold text-foreground">How to Create a Wizard</h2>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <ol className="space-y-3 text-sm text-muted-foreground">
              <NumberedStep n={1}>
                Go to the <a href={adminRoutes.wizards} className="text-nara-orange hover:underline font-medium">Wizards list</a> and click <span className="font-medium text-foreground">"Create Wizard"</span>.
              </NumberedStep>
              <NumberedStep n={2}>
                Enter a <span className="font-medium text-foreground">name</span>, <span className="font-medium text-foreground">slug</span> (URL handle), and optional description. The slug auto-generates from the name but can be customized.
              </NumberedStep>
              <NumberedStep n={3}>
                On the template detail page, <span className="font-medium text-foreground">add steps</span> from the available block types using the "Add Step" dropdown.
              </NumberedStep>
              <NumberedStep n={4}>
                <span className="font-medium text-foreground">Configure each step</span> with overrides such as title, subtitle, points, form fields, or action settings.
              </NumberedStep>
              <NumberedStep n={5}>
                <span className="font-medium text-foreground">Reorder steps</span> as needed using the up/down arrows in the steps editor.
              </NumberedStep>
              <NumberedStep n={6}>
                Toggle the template to <span className="font-medium text-foreground">"Active"</span> when ready. Only active templates are accessible via their public slug URL.
              </NumberedStep>
              <NumberedStep n={7}>
                Use the <span className="font-medium text-foreground">Preview</span> button in the header to test the wizard flow as a user would see it.
              </NumberedStep>
            </ol>
          </div>
        </section>

        {/* 4. How to Assign a Wizard to a User */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-5 w-5 text-nara-orange" />
            <h2 className="text-lg font-semibold text-foreground">How to Assign a Wizard to a User</h2>
          </div>
          <div className="bg-card border border-border rounded-xl p-5">
            <ol className="space-y-3 text-sm text-muted-foreground">
              <NumberedStep n={1}>
                Open a wizard <span className="font-medium text-foreground">template detail page</span>.
              </NumberedStep>
              <NumberedStep n={2}>
                In the <span className="font-medium text-foreground">"Create Wizard Instance"</span> panel, search for a user by name, email, or phone.
              </NumberedStep>
              <NumberedStep n={3}>
                Select the user from the dropdown results.
              </NumberedStep>
              <NumberedStep n={4}>
                Click <span className="font-medium text-foreground">"Create Instance"</span>. A frozen copy of the template is created for that user.
              </NumberedStep>
              <NumberedStep n={5}>
                <span className="font-medium text-foreground">Copy the generated link</span> and share it with the user. They can access it directly at <code className="bg-muted px-1.5 py-0.5 rounded text-xs">/wizard/:instanceId</code>.
              </NumberedStep>
              <NumberedStep n={6}>
                Track progress in the <span className="font-medium text-foreground">"Entries"</span> tab on the template detail page.
              </NumberedStep>
            </ol>
          </div>
        </section>

        {/* 5. Block Types Reference */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Blocks className="h-5 w-5 text-nara-orange" />
            <h2 className="text-lg font-semibold text-foreground">Block Types Reference</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BlockTypeCard
              icon={<Play className="h-5 w-5 text-purple-500" />}
              uiType="Splash"
              blockNames="splash, splash_screen"
              description="Welcome or intro screen. Typically the first step, showing branding and a brief overview of what the wizard does."
              accentColor="purple"
            />
            <BlockTypeCard
              icon={<FormInput className="h-5 w-5 text-blue-500" />}
              uiType="Form"
              blockNames="form, collect_name, collect_company_name, generic_form"
              description="Collects user data via input fields. Configure the fields array with name, label, type, required flag, and more."
              accentColor="blue"
            />
            <BlockTypeCard
              icon={<Zap className="h-5 w-5 text-amber-500" />}
              uiType="Action Confirmation"
              blockNames="action_confirmation, create_workspace, send_sms_invite"
              description="Triggers a backend action and shows loading/success states. Used for operations like creating a workspace or sending an invite."
              accentColor="amber"
            />
            <BlockTypeCard
              icon={<FileText className="h-5 w-5 text-green-500" />}
              uiType="Instruction"
              blockNames="instruction"
              description="Displays informational content or instructions to the user. Supports text-based content via the instructions config key."
              accentColor="green"
            />
            <BlockTypeCard
              icon={<Timer className="h-5 w-5 text-cyan-500" />}
              uiType="Polling"
              blockNames="polling, check_documents"
              description="Waits for an asynchronous condition to be met (e.g., document upload). Polls the backend at a configurable interval. Optionally skippable."
              accentColor="cyan"
            />
            <BlockTypeCard
              icon={<PartyPopper className="h-5 w-5 text-pink-500" />}
              uiType="Completion"
              blockNames="completion"
              description="Final step with a call-to-action button. Marks the end of the wizard flow. Configure the CTA label and action."
              accentColor="pink"
            />
          </div>
        </section>

        {/* 6. Step Configuration Reference */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Settings2 className="h-5 w-5 text-nara-orange" />
            <h2 className="text-lg font-semibold text-foreground">Step Configuration Reference</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Each step can have config overrides that control its behavior and appearance. These are set in the step editor on the template detail page.
          </p>

          {/* Common to all */}
          <ConfigGroup title="Common (all step types)" className="mb-4">
            <ConfigRow name="title" type="string" description="Display title shown at the top of the step" />
            <ConfigRow name="subtitle" type="string" description="Subtitle text shown below the title" />
            <ConfigRow name="points" type="number" description="XP points awarded when the user completes this step" />
            <ConfigRow name="achievementId" type="string" description="Achievement badge ID to unlock on completion" />
            <ConfigRow name="celebrate" type="boolean" description="Show a celebration animation when the step completes" />
            <ConfigRow name="showInEntryDetail" type="boolean" description="Show this step's collected data in the admin entry detail view" />
            <ConfigRow name="nextLabel" type="string" description='Custom label for the "Next" button (default: "Next")' />
            <ConfigRow name="backAllowed" type="boolean" description="Allow the user to navigate back to the previous step" />
            <ConfigRow name="autoAdvance" type="boolean" description="Automatically advance to the next step when this one completes" />
          </ConfigGroup>

          {/* Form steps */}
          <ConfigGroup title="Form Steps" className="mb-4">
            <ConfigRow name="fields" type="array" description="Array of field definitions. Each field has: name, label, type (text, email, tel, select, etc.), required, placeholder, and options (for select fields)" />
          </ConfigGroup>

          {/* Action steps */}
          <ConfigGroup title="Action Confirmation Steps" className="mb-4">
            <ConfigRow name="action" type="string" description="Backend action identifier to trigger (e.g., create_workspace, send_sms_invite)" />
            <ConfigRow name="loadingText" type="string" description="Text shown while the action is running" />
            <ConfigRow name="successText" type="string" description="Text shown after the action completes successfully" />
          </ConfigGroup>

          {/* Instruction steps */}
          <ConfigGroup title="Instruction Steps" className="mb-4">
            <ConfigRow name="instructions" type="string" description="The informational content to display to the user" />
          </ConfigGroup>

          {/* Polling steps */}
          <ConfigGroup title="Polling Steps" className="mb-4">
            <ConfigRow name="pollAction" type="string" description="Backend action to poll for completion status" />
            <ConfigRow name="pollIntervalMs" type="number" description="How often to poll in milliseconds (e.g., 3000 for every 3 seconds)" />
            <ConfigRow name="waitingText" type="string" description="Text shown while waiting for the condition to be met" />
            <ConfigRow name="skipLabel" type="string" description="Label for an optional skip button (omit to prevent skipping)" />
          </ConfigGroup>

          {/* Completion steps */}
          <ConfigGroup title="Completion Steps">
            <ConfigRow name="ctaLabel" type="string" description='Label for the call-to-action button (e.g., "Go to Dashboard")' />
            <ConfigRow name="ctaAction" type="string" description="Action to perform when the CTA is clicked (e.g., navigate to a URL)" />
          </ConfigGroup>
        </section>

        {/* 7. Important Notes */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Info className="h-5 w-5 text-nara-orange" />
            <h2 className="text-lg font-semibold text-foreground">Important Notes</h2>
          </div>
          <div className="space-y-3">
            <InfoBanner type="info">
              <strong>Template versioning:</strong> Changes to a template do not affect existing instances.
              Each instance uses a frozen snapshot of the template from the moment it was created.
              To apply changes, create new instances.
            </InfoBanner>
            <InfoBanner type="warning">
              <strong>Slug changes break URLs:</strong> If you change a template's slug (handle),
              any existing links using the old slug will stop working. Only change slugs if you're
              certain no active links are using them.
            </InfoBanner>
            <InfoBanner type="info">
              <strong>Inactive templates:</strong> Inactive templates can still have instances created
              manually via the admin panel. However, they won't be accessible through the public slug
              URL (<code className="bg-muted px-1 py-0.5 rounded text-xs">/wizard/s/:slug</code>).
            </InfoBanner>
          </div>
        </section>
      </div>
    </div>
  );
}

/* ---- Helper components ---- */

function ConceptCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-10 w-10 rounded-lg bg-nara-orange/10 flex items-center justify-center shrink-0">
          {icon}
        </div>
        <h3 className="font-semibold text-foreground">{title}</h3>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

function BlockTypeCard({
  icon,
  uiType,
  blockNames,
  description,
  accentColor,
}: {
  icon: React.ReactNode;
  uiType: string;
  blockNames: string;
  description: string;
  accentColor: string;
}) {
  const bgMap: Record<string, string> = {
    purple: "bg-purple-50 dark:bg-purple-950/30",
    blue: "bg-blue-50 dark:bg-blue-950/30",
    amber: "bg-amber-50 dark:bg-amber-950/30",
    green: "bg-green-50 dark:bg-green-950/30",
    cyan: "bg-cyan-50 dark:bg-cyan-950/30",
    pink: "bg-pink-50 dark:bg-pink-950/30",
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className={`h-10 w-10 rounded-lg ${bgMap[accentColor] || "bg-muted"} flex items-center justify-center shrink-0`}>
          {icon}
        </div>
        <div>
          <h3 className="font-semibold text-foreground">{uiType}</h3>
          <p className="text-xs text-muted-foreground font-mono">{blockNames}</p>
        </div>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

function NumberedStep({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex items-center justify-center h-6 w-6 rounded-full bg-nara-orange/10 text-nara-orange text-xs font-bold shrink-0 mt-0.5">
        {n}
      </span>
      <span className="leading-relaxed">{children}</span>
    </li>
  );
}

function ConfigGroup({ title, children, className }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-card border border-border rounded-xl overflow-hidden ${className || ""}`}>
      <div className="px-5 py-3 bg-muted/30 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      </div>
      <div className="divide-y divide-border">{children}</div>
    </div>
  );
}

function ConfigRow({ name, type, description }: { name: string; type: string; description: string }) {
  return (
    <div className="px-5 py-3 flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
      <div className="flex items-center gap-2 shrink-0 sm:w-40">
        <code className="text-sm font-mono font-medium text-foreground">{name}</code>
        <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{type}</span>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function InfoBanner({ type, children }: { type: "info" | "warning"; children: React.ReactNode }) {
  if (type === "warning") {
    return (
      <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-700 dark:text-amber-300 leading-relaxed">{children}</div>
      </div>
    );
  }
  return (
    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-start gap-3">
      <Info className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
      <div className="text-sm text-blue-700 dark:text-blue-300 leading-relaxed">{children}</div>
    </div>
  );
}
